import { toZonedTime } from 'date-fns-tz'
import { TFunction } from 'i18next'

import { SurveyResponder } from '@/api/communication/surveys/get-survey-responders'
import { EMPTY_VALUE } from '@/constants'
import { SurveyCommunicationType, SurveyCommunicationTypeEnum, SurveyQuestion } from '@/models'
import { formatDateOnly, formatTimeOnly } from '@/utils/date'
import { buildCsvFilename, downloadCsv } from '@/utils/file'

import { EMAIL_STATUSES, IN_APP_STATUSES, SMS_STATUSES } from './survey-recipients-table.constants'

export const getRelevantStatuses = (communicationType: SurveyCommunicationType) => {
  switch (communicationType) {
    case SurveyCommunicationTypeEnum.enum.email:
      return EMAIL_STATUSES
    case SurveyCommunicationTypeEnum.enum.sms:
      return SMS_STATUSES
    case SurveyCommunicationTypeEnum.enum['in-app']:
      return IN_APP_STATUSES
    default:
      return EMAIL_STATUSES
  }
}

interface ExportSurveyRecipientsCSVParams {
  responders: SurveyResponder[]
  questions: SurveyQuestion[]
  isInAppCommunication: boolean
  buildingName?: string
  timezone: string
  surveyId: number
  t: TFunction<['communication']>
}

const getQuestionFields = (questions: SurveyQuestion[], t: TFunction<['communication']>) => {
  if (questions.length > 1) {
    return questions.map((question, questionIndex) => ({
      label: t('communication:surveyDetails.recipientsTable.csv.question', {
        order: question.order,
      }),
      value: (row: SurveyResponder) => row.answerDetails?.[questionIndex]?.answer ?? '-',
    }))
  }
  return [
    {
      label: t('communication:surveyDetails.recipientsTable.columns.reply'),
      value: (row: SurveyResponder) => row.answerDetails?.[0]?.answer ?? '-',
    },
  ]
}

const formatUpdatedAt = (updatedAt: string | null, timezone: string) => {
  if (!updatedAt) return EMPTY_VALUE
  const zonedDate = toZonedTime(updatedAt, timezone)
  return `${formatDateOnly(zonedDate)} ${formatTimeOnly(zonedDate)}`
}

export const exportSurveyRecipientsCSV = ({
  responders,
  questions,
  isInAppCommunication,
  buildingName,
  timezone,
  surveyId,
  t,
}: ExportSurveyRecipientsCSVParams) => {
  const fields = [
    {
      label: t('communication:surveyDetails.recipientsTable.columns.resident'),
      value: (row: SurveyResponder) => `${row.user.firstName} ${row.user.lastName}`.trim(),
    },
    {
      label: t('communication:surveyDetails.recipientsTable.columns.apartment'),
      value: (row: SurveyResponder) => row.user.apartment || '-',
    },
    {
      label: t('communication:surveyDetails.recipientsTable.columns.status'),
      value: 'status',
    },
    ...getQuestionFields(questions, t),
    ...(isInAppCommunication
      ? [
          {
            label: t('communication:surveyDetails.recipientsTable.columns.comment'),
            value: (row: SurveyResponder) => row.comment || '-',
          },
        ]
      : []),
    {
      label: t('communication:surveyDetails.recipientsTable.columns.lastUpdated'),
      value: (row: SurveyResponder) => formatUpdatedAt(row.updatedAt, timezone),
    },
  ]

  downloadCsv({
    data: responders,
    fields,
    filename: buildCsvFilename({
      building: buildingName,
      context: [`ID-${surveyId}`],
      date: new Date(),
      page: 'surveys',
      timezone,
    }),
  })
}
